import { useCallback, useMemo } from 'react';
import { Button, Space, Typography } from 'antd';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAppDispatch, useAppState } from '../../store/hooks';
import { FieldItem } from './FieldItem';
import styles from './styles.module.css';

interface FieldPanelProps {
  visibleFieldIds: string[];
}

export function FieldPanel({ visibleFieldIds }: FieldPanelProps) {
  const { fieldOptions } = useAppState();
  const dispatch = useAppDispatch();

  const ordered = useMemo(
    () => [...fieldOptions].sort((a, b) => a.order - b.order),
    [fieldOptions],
  );
  const itemIds = useMemo(() => ordered.map((f) => f.id), [ordered]);
  const checkedCount = useMemo(() => ordered.filter((f) => f.checked).length, [ordered]);
  const allChecked = ordered.length > 0 && checkedCount === ordered.length;
  const noneChecked = checkedCount === 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleToggle = useCallback(
    (fieldId: string) => dispatch({ type: 'TOGGLE_FIELD', fieldId }),
    [dispatch],
  );

  const handleToggleAll = useCallback(() => {
    dispatch({ type: 'TOGGLE_ALL', checked: !allChecked });
  }, [dispatch, allChecked]);

  const handleInvert = useCallback(() => {
    dispatch({ type: 'INVERT_FIELDS' });
  }, [dispatch]);

  const handleFollowView = useCallback(() => {
    dispatch({ type: 'FOLLOW_VIEW_ORDER', visibleFieldIds });
  }, [dispatch, visibleFieldIds]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      dispatch({
        type: 'REORDER_FIELDS',
        activeId: String(active.id),
        overId: String(over.id),
      });
    },
    [dispatch],
  );

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        <Space size={4} wrap>
          <Button size="small" onClick={handleToggleAll} disabled={ordered.length === 0}>
            {allChecked ? '全不选' : '全选'}
          </Button>
          <Button size="small" onClick={handleInvert} disabled={ordered.length === 0}>
            反选
          </Button>
          <Button
            size="small"
            type="default"
            onClick={handleFollowView}
            disabled={visibleFieldIds.length === 0}
          >
            跟随视图顺序
          </Button>
        </Space>
        <Typography.Text className={styles.count} type="secondary">
          已选 {checkedCount}/{ordered.length}
        </Typography.Text>
      </div>

      {ordered.length === 0 ? (
        <div className={styles.empty}>当前数据表无字段</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <ul className={styles.list}>
              {ordered.map((field) => (
                <FieldItem key={field.id} field={field} onToggle={handleToggle} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {noneChecked && ordered.length > 0 && (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          至少需要勾选一个字段才能打印 / 导出
        </Typography.Text>
      )}
    </div>
  );
}
